import throttle from 'lodash/throttle';

import { registerFragment } from '@neptune/cx/registry';
import { DisposalSet, select } from '@neptune/cx/utils';
import {
    reinitActiveFacets,
    removeFromMultiParamMap,
    replaceInMultiParamMap, selectors,
    toMultiParamMap,
    toURLSearchParams,
    updateResults
} from './facets';
import './styles.scss';

const gc = new DisposalSet();

interface ChroaSearchLayoutConfiguration {
    pageParameterName: string;
    queryParameterName: string;
    sponsorIdParameterName: string;
    categoryParameterNames: string;
}

registerFragment<ChroaSearchLayoutConfiguration>('cbdce-search-layout', (fragEl, config, meta) => {
    const searchLayoutEl = select<HTMLDivElement>(fragEl, '.cbdce-search-layout');
    const sortSelectEl = (searchLayoutEl
        .querySelector('.cbdce-search-layout__sort select') as HTMLSelectElement
    ) ?? null;

    const queryInputEl = (searchLayoutEl
        .querySelector('input.search-bar-keywords-input') as HTMLInputElement
    ) ?? null;

    const sortParamEl = (searchLayoutEl
        .querySelector('input.sort-parameter-name') as HTMLInputElement
    ) ?? null;

    const queryFormEl = queryInputEl?.closest('form') ?? null;

    const queryParamName = queryInputEl?.name || 'q';
    const sortParamName = sortParamEl?.value || 'sort';
    const pageParamName = config.pageParameterName || 'start';

    if (sortSelectEl) {
        const sortHelperEl = document.createElement('span');
        sortHelperEl.classList.add('cbdce-search-layout__sort-helper');
        sortHelperEl.setAttribute('aria-hidden', 'true');
        sortSelectEl.parentElement?.appendChild(sortHelperEl);
        const resizeSort = () => {
            const selectedOptionEl = sortSelectEl.options[sortSelectEl.selectedIndex];
            sortHelperEl.innerHTML = selectedOptionEl.innerText;
            requestAnimationFrame(() => {
                sortSelectEl.style.setProperty(
                    "--cbdce-search-layout__sort--width",
                    `${sortHelperEl.offsetWidth}px`
                );
            })

        };
        resizeSort();
        setTimeout(resizeSort, 1000);
        setTimeout(resizeSort, 5000);

        (gc.register(sortSelectEl)
            .addListener('change', (event) => {
                resizeSort();
                const multiParamMap = toMultiParamMap(new URLSearchParams(window.location.search));
                const value = sortSelectEl.selectedOptions[0].value;
                if (!value || value.trim() === '') {
                    removeFromMultiParamMap(multiParamMap, sortParamName);
                } else {
                    replaceInMultiParamMap(multiParamMap, sortParamName, value);
                }
                removeFromMultiParamMap(multiParamMap, pageParamName, null);
                const params = toURLSearchParams(multiParamMap);
                const nextUrl = `${window.location.pathname}?${params}`;
                updateResults(searchLayoutEl, nextUrl, pageParamName);
            })
        );
    }


    if (queryInputEl && queryFormEl) {
        reinitActiveFacets(searchLayoutEl, queryInputEl, pageParamName);

        let lastQueryValue = queryInputEl.value;
        const onQueryUpdate = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (queryInputEl.value.trim() === lastQueryValue.trim()) {
                return;
            } // prevent unnecessary updates
            lastQueryValue = queryInputEl.value;
            const multiParamMap = toMultiParamMap(new URLSearchParams(window.location.search));
            const value = queryInputEl.value;
            if (!value || value.trim() === '') {
                removeFromMultiParamMap(multiParamMap, queryParamName);
            } else {
                replaceInMultiParamMap(multiParamMap, queryParamName, value);
            }
            removeFromMultiParamMap(multiParamMap, pageParamName, null);
            const params = toURLSearchParams(multiParamMap);
            const nextUrl = `${window.location.pathname}?${params}`;
            updateResults(searchLayoutEl, nextUrl, pageParamName);
        };
        const onQueryUpdateWrapped = throttle(onQueryUpdate, 500, { trailing: true, leading: true });

        (gc.register(queryInputEl)
            .addListener('keyup', onQueryUpdateWrapped)
            .addListener('blur', onQueryUpdateWrapped)
        );
        (gc.register(queryFormEl)
            .addListener('submit', e => {
                e.stopImmediatePropagation();
                onQueryUpdate(e);
            })
        );
    }
});


if (import.meta.hot) {
    import.meta.hot.accept();
    import.meta.hot.dispose(() => gc.disposeAll());
}
